<!-- Tunnel Status Check -->
@if (tunnelChecking()) {
    <div class="tunnel-status-message">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Checking device tunnel connection...</span>
    </div>
}

<!-- Tunnel Not Connected -->
@if (!tunnelChecking() && !tunnelConnected()) {
    <div class="tunnel-offline-wrapper">
        <app-section-card class="tunnel-offline-card" title="Device Tunnel Not Connected" icon="fa fa-cloud-slash" tone="warning">
            <p>This device does not have an active tunnel connection to the cloud.</p>
            <p class="text-sm text-color-secondary mt-2">
                The device must be running a firmware version that supports cloud tunnel connections.
                Ensure the device is online and has checked in recently.
            </p>
            <div>
                <button pButton type="button" class="p-button-outlined mt-3" (click)="tunnelChecking.set(true); checkTunnelStatus()"><i class="pi pi-refresh p-button-icon" aria-hidden="true"></i><span class="p-button-label">Retry</span></button>
            </div>
        </app-section-card>
    </div>
}

<!-- Tunnel Connected - Show Console -->
@if (!tunnelChecking() && tunnelConnected()) {
    <!-- Connection Form -->
    @if (showConnectionForm()) {
        <div class="connection-form-wrapper">
            <app-section-card class="connection-card" title="Connect to Remote Device" subtitle="Opens an SSH, Telnet or CTP session from the device over its cloud tunnel" icon="pi pi-server">
                <span badge class="tunnel-badge"><i class="pi pi-cloud" aria-hidden="true"></i> Cloud Tunnel</span>
                <div class="form-body">
                    <div class="field">
                        <label>Protocol</label>
                        <p-selectbutton [options]="protocolOptions" [ngModel]="protocol()" (ngModelChange)="protocol.set($event); onProtocolChange()" optionLabel="label" optionValue="value" [allowEmpty]="false" />
                    </div>
                    <div class="field">
                        <label for="rc-hostname">Hostname / IP</label>
                        <input pInputText id="rc-hostname" type="text" [ngModel]="hostname()" (ngModelChange)="hostname.set($event)" placeholder="192.168.1.100" [disabled]="isConnecting()" (keydown.enter)="connect()" class="w-full" />
                    </div>
                    <div class="field">
                        <label for="rc-port">Port</label>
                        <input pInputText id="rc-port" type="number" [ngModel]="port()" (ngModelChange)="port.set(+$event)" min="1" max="65535" [disabled]="isConnecting()" (keydown.enter)="connect()" class="w-full" />
                    </div>
                    @if (protocol() === 'ssh') {
                        <div class="field">
                            <label for="rc-username">Username</label>
                            <input pInputText id="rc-username" type="text" [ngModel]="username()" (ngModelChange)="username.set($event)" placeholder="admin" [disabled]="isConnecting()" (keydown.enter)="connect()" class="w-full" />
                        </div>
                        <div class="field">
                            <label for="rc-password">Password</label>
                            <p-password [ngModel]="password()" (ngModelChange)="password.set($event)" [feedback]="false" [toggleMask]="true" placeholder="••••••••" [disabled]="isConnecting()" class="w-full" inputStyleClass="w-full" />
                        </div>
                    }
                    <p-message *ngIf="device()?.name" severity="info" class="w-full mt-1">
                        <span class="text-sm">Connecting through <strong>{{ displayName(device()) }}</strong> via cloud tunnel</span>
                    </p-message>
                    <div class="form-actions">
                        <button pButton type="button" (click)="connect()" [disabled]="isConnecting() || !hostname()" [loading]="isConnecting()">@if (isConnecting()) {<i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>} @else {<i class="pi pi-play p-button-icon" aria-hidden="true"></i>}<span class="p-button-label">{{ isConnecting() ? 'Connecting...' : 'Connect' }}</span></button>
                    </div>
                    @if (errorMessage()) {
                        <div class="error-message"><i class="pi pi-exclamation-triangle"></i> {{ errorMessage() }}</div>
                    }
                </div>
            </app-section-card>
        </div>
    }

    <!-- Host Key Verification Dialog -->
    <p-dialog header="Host Key Verification" [visible]="showHostKeyPrompt()" [modal]="true" [closable]="false" [style]="{ width: '500px' }">
        <div class="host-key-content">
            <p>The authenticity of the host cannot be established.</p>
            @if (pendingHostKeyType()) {
                <div class="host-key-info">
                    <div class="key-detail"><strong>Key type:</strong> {{ pendingHostKeyType() }}</div>
                    <div class="key-detail">
                        <strong>Fingerprint:</strong> <code>{{ pendingHostKeyFingerprint() }}</code>
                    </div>
                </div>
            } @else {
                <p class="muted">Waiting for host key information...</p>
            }
            <p>Are you sure you want to continue connecting?</p>
        </div>
        <ng-template #footer>
            <button pButton class="p-button-text p-button-danger" (click)="acceptHostKey(false)"><i class="pi pi-times p-button-icon" aria-hidden="true"></i><span class="p-button-label">Reject</span></button>
            <button pButton (click)="acceptHostKey(true)" [disabled]="!pendingHostKeyFingerprint()"><i class="pi pi-check p-button-icon" aria-hidden="true"></i><span class="p-button-label">Accept</span></button>
        </ng-template>
    </p-dialog>

    <!-- Terminal Session -->
    @if (sessionId() && !showConnectionForm()) {
        <div class="terminal-wrapper" (click)="focusTerminal()">
            <div class="terminal-header">
                <span class="status-dot ml-2" [class.connected]="isConnected()" [class.error]="sessionStatus() === 'Error'"></span>
                <span class="session-label">{{ hostname() }}:{{ port() }}</span>
                <span class="session-protocol">{{ protocolDisplayName() }}</span>
                <span class="tunnel-indicator"><i class="pi pi-cloud"></i> via {{ displayName(device()) }}</span>
                <span class="spacer"></span>
                <button pButton severity="danger" class="m-2" outlined size="small" (click)="disconnect(); $event.stopPropagation()" pTooltip="Disconnect" tooltipPosition="left"><i class="fa fa-xmark p-button-icon" aria-hidden="true"></i>Disconnect</button>
            </div>
            <div class="terminal-container" #terminalContainer></div>
        </div>
    }
}
